import React, { useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import * as XLSX from "xlsx";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Search from "@material-ui/icons/Search";
import CheckCircle from "@material-ui/icons/CheckCircle";
import DoneAll from "@material-ui/icons/DoneAll";
import EventBusy from "@material-ui/icons/EventBusy";
import Cancel from "@material-ui/icons/Cancel";
import AttachMoney from "@material-ui/icons/AttachMoney";
import Snackbar from "components/Snackbar/Snackbar.js";
import LoadingAnimation from "components/Animations/loading";
import { useAsyncEffect } from "use-async-effect";
import { redirectToLogout } from "common/SharedFn";
import { getMonthlyPackagePurchases } from "../../network/ApiAxios";

// Every customer who bought the monthly package: where each purchase stands, what is left
// of it, filtered by status and purchase date. The package itself is edited on the Monthly
// Package page.

const STATUSES = {
  ACTIVE: { label: "Active", color: "#2e7d32", background: "#e8f5e9", icon: <CheckCircle />, swatch: "#2dce89" },
  USED_UP: { label: "Used up", color: "#8a6d00", background: "#fff8e1", icon: <DoneAll />, swatch: "#fb6340" },
  EXPIRED: { label: "Expired", color: "#6b7280", background: "#f1f3f5", icon: <EventBusy />, swatch: "#8898aa" },
  CANCELLED: { label: "Cancelled", color: "#c62828", background: "#fdecea", icon: <Cancel />, swatch: "#f5365c" },
};
const DAY_MS = 24 * 60 * 60 * 1000;

const formatBd = (value) => `${(Number(value) || 0).toFixed(3)} BD`;
const formatDate = (millis) =>
  millis ? new Date(millis).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";
// "yyyy-mm-dd" from a date input, read as a local day.
const dayStart = (value) => (value ? new Date(`${value}T00:00:00`).getTime() : null);
const dayEnd = (value) => (value ? new Date(`${value}T23:59:59.999`).getTime() : null);

const MonthlyPackageSubscribers = () => {
  const classes = useStyles();
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [notifMessage, setNotifMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const notifyError = (message) => {
    setNotifMessage(message);
    setShowError(true);
    setTimeout(() => setShowError(false), 4000);
  };

  useAsyncEffect(async () => {
    setIsLoading(true);
    try {
      const response = await getMonthlyPackagePurchases();
      if (response.data.result.isSuccess) {
        setPurchases(response.data.data || []);
      } else {
        notifyError(response.data.result.description || "Failed to load package subscribers");
        if (response.data.result.code === -2) redirectToLogout();
      }
    } catch (ex) {
      notifyError("Failed to load package subscribers");
    }
    setIsLoading(false);
  }, []);

  // Search and dates narrow everything on the page; the status filter narrows only the
  // table, so the cards keep showing how the selected period splits by status.
  const inRange = useMemo(() => {
    const from = dayStart(fromDate);
    const to = dayEnd(toDate);
    const term = searchTerm.trim().toLowerCase();
    return purchases.filter((p) => {
      if (from !== null && p.purchasedOn < from) return false;
      if (to !== null && p.purchasedOn > to) return false;
      if (!term) return true;
      return [p.customerName, p.mobileNumber, p.userIdForm].some((v) => String(v || "").toLowerCase().includes(term));
    });
  }, [purchases, fromDate, toDate, searchTerm]);

  const rows = useMemo(
    () => inRange.filter((p) => statusFilter === "all" || p.status === statusFilter).map(makeRow),
    [inRange, statusFilter]
  );

  const counts = useMemo(() => {
    const byStatus = { ACTIVE: 0, USED_UP: 0, EXPIRED: 0, CANCELLED: 0 };
    let revenue = 0;
    inRange.forEach((p) => {
      byStatus[p.status] = (byStatus[p.status] || 0) + 1;
      // A cancelled package was refunded in full.
      if (p.status !== "CANCELLED") revenue += Number(p.total) || 0;
    });
    return { byStatus, revenue };
  }, [inRange]);

  const filtersSet = searchTerm || statusFilter !== "all" || fromDate || toDate;
  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setFromDate("");
    setToDate("");
  };

  const download = () => {
    const sheet = XLSX.utils.json_to_sheet(rows.map((r) => ({
      "Customer ID": r.userIdForm,
      Customer: r.customerName,
      Mobile: r.mobileNumber,
      Bought: r.purchasedOnText,
      "Valid until": r.endDateText,
      "Bags used": r.bagsUsed,
      "Bags included": r.bagsIncluded,
      "Shoes used": r.shoesUsed,
      "Shoes included": r.shoesIncluded,
      "Paid (incl. VAT)": Number(r.total),
      Payment: r.paymentMethod,
      Status: r.statusText,
      "Cancelled on": formatDate(r.cancelledOn),
    })));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Subscribers");
    XLSX.writeFile(book, `package-subscribers-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const statCards = [
    ...Object.keys(STATUSES).map((key) => ({
      label: STATUSES[key].label,
      value: counts.byStatus[key] || 0,
      icon: STATUSES[key].icon,
      color: STATUSES[key].swatch,
      status: key,
    })),
    { label: "Paid (excl. refunds)", value: formatBd(counts.revenue), icon: <AttachMoney />, color: "#5e72e4" },
  ];

  return (
    <Container maxWidth={false} component={Box} marginTop="-4.5rem" classes={{ root: classes.containerRoot }}>
      <Grid container spacing={2} style={{ marginBottom: "0.5rem", position: "relative", zIndex: 1 }}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} lg key={stat.label}>
            <Card
              className={`${classes.statCard} ${stat.status ? classes.clickable : ""} ${stat.status && statusFilter === stat.status ? classes.selected : ""}`}
              onClick={stat.status ? () => setStatusFilter(statusFilter === stat.status ? "all" : stat.status) : undefined}
            >
              <Box className={classes.iconShape} style={{ backgroundColor: stat.color }}>{stat.icon}</Box>
              <Box minWidth={0}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>{stat.label}</Typography>
                <Typography variant="h4" style={{ fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box className={classes.filterBox}>
        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: "10px" }}>
          <Box style={{ flex: "1 1 240px", minWidth: "200px" }}>
            <TextField
              fullWidth
              placeholder="Search by name, mobile or customer ID..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="disabled" /></InputAdornment> }}
            />
          </Box>
          <Box style={{ flex: "0 1 150px", minWidth: "130px" }}>
            <TextField
              select
              fullWidth
              label="Status"
              variant="outlined"
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="all">All Statuses</option>
              {Object.keys(STATUSES).map((key) => (
                <option key={key} value={key}>{STATUSES[key].label}</option>
              ))}
            </TextField>
          </Box>
          <Box style={{ flex: "0 1 150px", minWidth: "130px" }}>
            <TextField
              fullWidth
              label="Bought From"
              type="date"
              variant="outlined"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toDate || undefined }}
            />
          </Box>
          <Box style={{ flex: "0 1 150px", minWidth: "130px" }}>
            <TextField
              fullWidth
              label="Bought To"
              type="date"
              variant="outlined"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fromDate || undefined }}
            />
          </Box>
          {filtersSet && (
            <Box style={{ flex: "0 0 auto" }}>
              <Button onClick={clearFilters} className={classes.actionButton} style={{ height: 40 }}>Clear</Button>
            </Box>
          )}
          <Box style={{ flex: "0 0 auto" }}>
            <Button
              variant="contained"
              color="primary"
              disabled={isLoading || rows.length === 0}
              onClick={download}
              className={classes.actionButton}
              style={{ height: 40, padding: "0 12px" }}
            >
              Download
            </Button>
          </Box>
        </Box>
      </Box>

      <Card className={classes.cardRoot}>
        <div className="table-responsive">
          <BootstrapTable
            keyField="id"
            data={rows}
            columns={columns}
            bootstrap4={true}
            bordered={false}
            hover={true}
            pagination={pagination}
            defaultSorted={[{ dataField: "purchasedOnText", order: "desc" }]}
            noDataIndication={() =>
              isLoading ? (
                <Box component={Grid} container justifyContent="center">
                  <div className="text-center" style={{ width: "20%" }}><LoadingAnimation /></div>
                </Box>
              ) : (
                <Box padding="1.5rem" textAlign="center">
                  <Typography variant="h5" style={{ fontWeight: 600 }}>
                    {purchases.length === 0 ? "No one has bought the package yet" : "No subscribers match these filters"}
                  </Typography>
                </Box>
              )
            }
          />
        </div>
      </Card>

      <Snackbar place="tr" color="rose" message={notifMessage} open={showError} closeNotification={() => setShowError(false)} close />
    </Container>
  );
};

// Used of included, with a thin bar so a full package reads at a glance.
const usageCell = (used, included) => {
  const share = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
  return (
    <Box minWidth={84}>
      <span style={{ fontWeight: 600 }}>{used}</span>
      <span style={{ color: "#8898aa" }}> / {included}</span>
      <Box style={{ height: 4, borderRadius: 2, backgroundColor: "#edf0f7", marginTop: 4 }}>
        <Box style={{ height: 4, borderRadius: 2, width: `${share}%`, backgroundColor: share >= 100 ? "#fb6340" : "#5e72e4" }} />
      </Box>
    </Box>
  );
};

const makeRow = (p) => {
  const status = STATUSES[p.status] || { label: p.status, color: "#6b7280", background: "#f1f3f5" };
  const daysLeft = p.status === "ACTIVE" ? Math.max(0, Math.ceil((p.endDate - Date.now()) / DAY_MS)) : null;
  return {
    ...p,
    purchasedOnText: formatDate(p.purchasedOn),
    endDateText: formatDate(p.endDate),
    daysLeft,
    statusText: status.label,
    statusStyle: status,
  };
};

const columns = [
  {
    dataField: "customerName",
    text: "Customer",
    sort: true,
    formatter: (cell, row) => (
      <Box>
        <div style={{ fontWeight: 600 }}>{row.customerName || "—"}</div>
        <div style={{ color: "#8898aa", fontSize: "0.8125rem" }}>{row.userIdForm} · {row.mobileNumber}</div>
      </Box>
    ),
    style: { whiteSpace: "normal" },
  },
  { dataField: "purchasedOnText", text: "Bought", sort: true, sortValue: (cell, row) => row.purchasedOn },
  {
    dataField: "endDateText",
    text: "Valid until",
    sort: true,
    sortValue: (cell, row) => row.endDate,
    formatter: (cell, row) => (
      <Box>
        <div>{row.endDateText}</div>
        {row.daysLeft !== null && (
          <div style={{ color: row.daysLeft <= 5 ? "#fb6340" : "#8898aa", fontSize: "0.8125rem" }}>
            {row.daysLeft === 1 ? "1 day left" : `${row.daysLeft} days left`}
          </div>
        )}
      </Box>
    ),
  },
  { dataField: "bagsUsed", text: "Bags used", sort: true, formatter: (cell, row) => usageCell(row.bagsUsed, row.bagsIncluded) },
  { dataField: "shoesUsed", text: "Shoes used", sort: true, formatter: (cell, row) => usageCell(row.shoesUsed, row.shoesIncluded) },
  {
    dataField: "total",
    text: "Paid",
    sort: true,
    sortFunc: (a, b, order) => (order === "asc" ? Number(a) - Number(b) : Number(b) - Number(a)),
    formatter: (cell) => <span style={{ fontWeight: 600 }}>{formatBd(cell)}</span>,
  },
  { dataField: "paymentMethod", text: "Payment", sort: true },
  {
    dataField: "statusText",
    text: "Status",
    sort: true,
    formatter: (cell, row) => (
      <span style={{
        color: row.statusStyle.color,
        backgroundColor: row.statusStyle.background,
        fontWeight: 700,
        borderRadius: 6,
        padding: "3px 10px",
        whiteSpace: "nowrap",
        fontSize: "0.8125rem",
      }}>
        {row.statusText}
      </span>
    ),
  },
];

export default MonthlyPackageSubscribers;

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPage: 25,
});

// Same card language as the POS sales screen.
const useStyles = makeStyles((theme) => ({
  containerRoot: {
    [theme.breakpoints.up("md")]: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  cardRoot: {
    marginBottom: "1.25rem",
    borderRadius: "15px",
    boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
    overflow: "hidden",
  },
  statCard: {
    borderRadius: "15px",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "1rem",
    border: "2px solid transparent",
    transition: "transform 0.2s, border-color 0.2s",
    "&&": {
      marginBottom: 0,
    },
  },
  clickable: {
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
  selected: {
    borderColor: theme.palette.primary.main,
  },
  iconShape: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: "0.875rem",
    color: "#fff",
  },
  filterBox: {
    padding: "1rem 1.25rem",
    backgroundColor: "#fff",
    borderRadius: "15px",
    marginBottom: "1.25rem",
    boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .1)",
  },
  actionButton: {
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    whiteSpace: "nowrap",
  },
}));
